import { useState, useEffect, lazy, Suspense } from "react";
import {
  Route,
  RouteComponentProps,
  BrowserRouter as Router,
  Switch,
  useLocation,
} from "react-router-dom";
import "./App.css";
import { profJimBrandColors } from "./theme/variants";
import { analytics } from "./utils/analytics";
import { CircularProgress, Stack } from "@mui/material";
import LegacyInteractiveGenerator from "./components/legacyInteractiveGenerator";

const apiBaseURL = {
  qa: "https://testing2-profjimapi.azurewebsites.net/v1/",
  prod: "https://create-api.profjim.com/v1/",
};

const GamesLayout = lazy(() => import("./components/GamesLayout"));
const CodeEditorAndRunner = lazy(
  () => import("./components/Interactives/CodeEditorAndRunner")
);
const CreatiumIframeWrapper = lazy(
  () => import("./components/Interactives/CreatiumIframeWrapper")
);
const GameViewer = lazy(() => import("./components/Interactives/GameViewer"));
const GameViewerInteractiveElement = lazy(
  () => import("./components/Interactives/GameViewerInteractiveElement")
);
const Page404 = lazy(() => import("./components/Page404"));
const DragDropConfigure = lazy(
  () => import("./components/dragdrop/DragDropConfigure")
);
const DragDropGames = lazy(() => import("./components/dragdrop/DragDropGame"));
const PreviewWrapper = lazy(
  () => import("./components/Interactives/interactives-preview/PreviewWrapper")
);

function App() {
  const handleLoadCustomFonts = (
    userFonts: { url: string; fontName: string }[]
  ) => {
    const fontUrls = userFonts.map((font) => font.url);
    const fontNames = userFonts.map((font) => font.fontName);
    //@ts-ignore
    window.fontFamilyList = fontNames;
    fontUrls.forEach((url) => {
      if (url.includes("https://no-need") || url.includes("Blank")) {
        return;
      }

      const link = document.createElement("link");
      link.href = url;
      link.rel = "stylesheet";
      link.crossOrigin = "anonymous";

      link.type = "text/css";
      link.setAttribute("asynchrounous", "true");

      document.head.appendChild(link);
    });
  };

  const fetchCustomFonts = async () => {
    const API_BASE_URL = window.location.hostname.includes("dev-games")
      ? apiBaseURL.prod
      : apiBaseURL.qa;

    const fetchWithRetry = async (
      url: string,
      retryCount = 3,
      retryDelay = 1000
    ) => {
      for (let attempt = 1; attempt <= retryCount; attempt++) {
        try {
          const response = await fetch(url, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          });
          console.log("response", response);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const data = await response.json();
          return data.data.fonts.map((font: any) => ({
            url: font.generatedCss,
            fontName: font.name,
          }));
        } catch (error) {
          console.error(`Attempt ${attempt} failed:`, error);

          const isLastAttempt = attempt === retryCount;
          if (!isLastAttempt) {
            await new Promise((resolve) => setTimeout(resolve, retryDelay));
          }
        }
      }
    };

    const fonts = await fetchWithRetry(
      `${API_BASE_URL}Admin/GetAllCustomFonts`
    );
    if (fonts) {
      handleLoadCustomFonts(fonts);
    }
  };

  useEffect(() => {
    fetchCustomFonts();
    const searchParams = new URLSearchParams(window.location.search);
    const eventsData = searchParams.get("eventsData");
    if (eventsData) {
      try {
        const decodedEventsData = decodeURIComponent(eventsData);
        const parsedEventsData = JSON.parse(decodedEventsData);
        const parentTraceId = parsedEventsData.traceId;
        analytics.identity({
          ...parsedEventsData,
          parentTraceId,
        });
      } catch (error) {
        console.error("Error in identity:", error);
      }
    }
  }, []);

  const Interactive = () => {
    const location = useLocation();
    const urlParams = new URLSearchParams(location.search);
    const codeFromUrl = urlParams.get("code");
    const hideFullScreen = codeFromUrl != null;

    return (
      <div
        className="mx-auto"
        style={{
          backgroundColor: profJimBrandColors.white,
          minHeight: hideFullScreen ? "0vh" : "100vh",
        }}
      >
        <CodeEditorAndRunner />
      </div>
    );
  };

  const Flashcards = () => (
    <div className="container mx-auto px-4">
      <h1 className="text-4xl font-bold mb-8 text-center text-indigo-800">
        Flashcards
      </h1>
      <div className="bg-white rounded-lg shadow-lg p-6">
        <Flashcards />
        <div style={{ margin: "100px" }}></div>
      </div>
    </div>
  );

  const LoadingSpinner = () => {
    const location = useLocation();
    const isPreviewRoute = location.pathname === '/preview';
    const bgColor = isPreviewRoute ? "#ebebeb" : profJimBrandColors.white;
    const loaderColor = isPreviewRoute ? "#083738" : profJimBrandColors.orange;
    
    return (
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        style={{ backgroundColor: bgColor, position: "absolute", inset: 0 }}
      >
        <CircularProgress
          size={60}
          sx={{ color: loaderColor }}
        />
      </Stack>
    );
  };

  return (
    <div className="App">
      <Router>
        <Suspense fallback={<LoadingSpinner />}>
          <Switch>
            <Route
              path="/"
              exact
              render={(props: RouteComponentProps) => (
                <GamesLayout>
                  <DragDropGames />
                </GamesLayout>
              )}
            />
            <Route
              path="/interactive"
              exact
              render={() => (
                <div
                  className="mx-auto"
                  style={{
                    backgroundColor: profJimBrandColors.white,
                    minHeight: "100vh",
                  }}
                >
                  <CodeEditorAndRunner />
                </div>
              )}
            />
            <Route
              path="/legacy-interactives"
              exact
              render={() => (
                <div
                  className="mx-auto"
                  style={{
                    backgroundColor: profJimBrandColors.white,
                    minHeight: "100vh",
                  }}
                >
                  <LegacyInteractiveGenerator />
                </div>
              )}
            />
            <Route
              path="/preview"
              exact
              render={() => (
                <div
                  className="mx-auto"
                  style={{
                    backgroundColor: profJimBrandColors.white,
                    minHeight: "100vh",
                  }}
                >
                  <PreviewWrapper />
                </div>
              )}
            />
            <Route path="/flashcards" exact render={() => <Flashcards />} />
            <Route
              path="/game-viewer/:gameId"
              exact
              render={() => <GameViewer />}
            />
            <Route
              path="/interactive-viewer/:gameId"
              exact
              render={() => <GameViewerInteractiveElement />}
            />
            <Route
              path="/configure"
              exact
              render={(props: RouteComponentProps) => (
                <GamesLayout>
                  <DragDropConfigure />
                </GamesLayout>
              )}
            />
            <Route render={() => <Page404 />} />
          </Switch>
        </Suspense>
      </Router>
      <div
        id="game-viewer-container"
        style={{
          fontFamily: "Montserrat",
        }}
      ></div>
    </div>
  );
}

export default App;
